import type { UseCustomerBiometryData } from '~/store-v2/contract-formalization/useCustomerBiometry'
import { computed, type Ref } from 'vue'

const cancellationAlertConfig = {
  INTERNAL_POLICY: {
    titleKey: 'customer.canceled.internal_policy_title',
    descriptionKey: 'customer.canceled.internal_policy_description',
  },
  EXPIRED: {
    titleKey: '',
    descriptionKey: '',
  },
  REQUESTED_BY_CUSTOMER: {
    titleKey: 'customer.canceled.requested_by_customer_title',
    descriptionKey: 'customer.canceled.requested_by_customer_description',
  },
} as const

type CancellationType = keyof typeof cancellationAlertConfig

interface CancellationAlert {
  title: string
  description: string
}

/**
 * Composable to get the cancellation alert configuration
 * based on the financing cancellation type
 * @param biometryData Customer biometry data (contains formalization with cancellation_type)
 * @returns Computed that returns the alert title and description or null
 */
export function useCancellationAlert(biometryData: Ref<UseCustomerBiometryData | undefined>) {
  const { t } = useI18n()

  const cancellationAlert = computed<CancellationAlert | null>(() => {
    const cancellationTypeRaw = biometryData.value?.cancellation_type as string | undefined

    if (!cancellationTypeRaw) {
      return null
    }

    const cancellationType = cancellationTypeRaw.toUpperCase() as CancellationType

    if (!cancellationAlertConfig[cancellationType]) {
      return null
    }

    const config = cancellationAlertConfig[cancellationType]

    // EXPIRED não exibe mensagem, apenas retorna null
    if (cancellationType === 'EXPIRED') {
      return null
    }

    // Se não tem título, não exibe
    if (!config.titleKey) {
      return null
    }

    return {
      title: t(config.titleKey),
      description: config.descriptionKey ? t(config.descriptionKey) : '',
    }
  })

  return {
    cancellationAlert,
  }
}
